import { type AdminUserListQuery, AdminUserListResponseSchema } from '@cult/schemas';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../../../lib/api-client';
import { adminKeys } from '../../admin-keys';

// Filter fields only — pagination (limit/offset) is appended per call site. Also feeds the CSV export.
export const buildUserParams = (filters: AdminUserListQuery): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.role) for (const r of filters.role) params.append('role', r);
  if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));
  if (filters.q) params.set('q', filters.q);
  if (filters.includeBalance) params.set('includeBalance', 'true');
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  return params;
};

const fetchUsers = async (params: URLSearchParams) => {
  const raw = await apiFetch<unknown>(`/admin/users?${params.toString()}`);
  return AdminUserListResponseSchema.parse(raw);
};

// Numbered pagination — backs the admin Users table.
export const useAdminUsers = (filters: AdminUserListQuery, page: number, pageSize: number) => {
  const offset = (page - 1) * pageSize;
  const query: AdminUserListQuery = { ...filters, offset, limit: pageSize };
  return useQuery({
    queryKey: adminKeys.users(query),
    queryFn: () => {
      const params = buildUserParams(filters);
      params.set('limit', String(pageSize));
      params.set('offset', String(offset));
      return fetchUsers(params);
    },
    placeholderData: keepPreviousData,
  });
};

// Offset-based infinite scroll — backs the UserSelect dropdown (load-more on scroll).
export const useAdminUsersInfinite = (filters: AdminUserListQuery) =>
  useInfiniteQuery({
    queryKey: adminKeys.users(filters),
    queryFn: ({ pageParam }: { pageParam: number }) => {
      const params = buildUserParams(filters);
      params.set('limit', String(filters.limit ?? 20));
      params.set('offset', String(pageParam));
      return fetchUsers(params);
    },
    initialPageParam: 0,
    // The next page starts after everything loaded so far, until we reach the total.
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.users.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
